import { Alert, SpaceBetween } from '@amzn/awsui-components-react';
import React from 'react';

import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { MainframeAWSCosts, MainframeMigrationCosts, MainframeOnPremCosts, SummaryRow, SummaryTabProps } from './MainframeTypes';
import { MainframeSummaryCostColumnDefinitions } from './SummaryCostColumnDefinitions';

function onPremCostsToRows(currency: string, costs: MainframeOnPremCosts): SummaryRow[] {
    return [
        {
            description: constants.TOTAL_INSTALLED_MIPS,
            value: costs.totalInstalledMIPS,
        },
        {
            currency,
            description: constants.HARDWARE,
            value: costs.hardwareCost,
        },
        {
            currency,
            description: constants.SOFTWARE_IBM_3RD_PARTY,
            value: costs.softwareCost,
        },
        {
            currency,
            description: constants.PERSONNEL,
            value: costs.personnelCost,
        },
        {
            currency,
            description: constants.CONNECTIVITY,
            value: costs.connectivityCost,
        },
        {
            currency,
            description: constants.OCCUPANCY,
            value: costs.occupancyCost,
        },
        {
            currency,
            description: constants.DISASTER_RECOVERY,
            value: costs.disasterRecoveryCost,
        },
        {
            currency,
            description: constants.UNALLOCATED,
            value: costs.unallocatedCost,
        },
        {
            currency,
            description: constants.TOTAL,
            format: {
                textStyle: 'h2',
            },
            value: costs.totalCost,
        },
    ];
}

function migrationCostsToRows(currency: string, costs: MainframeMigrationCosts): SummaryRow[] {
    return [
        {
            description: constants.NUMBER_OF_LOC,
            value: costs.numberOfLinesOfCode,
        },
        {
            description: constants.NUMBER_OF_TEST_CASES,
            value: costs.numberOfTestCases,
        },
        {
            description: constants.NUMBER_OF_INTEGRATED_DEPENDENCIES,
            value: costs.numberOfIntegratedDependencies,
        },
        {
            description: constants.PROJECT_DURATION,
            value: costs.projectDurationInYears,
        },
        {
            currency,
            description: constants.SI_PROSERVE_LABOR_COST,
            value: costs.laborCost,
        },
        {
            currency,
            description: constants.TOOL_LICENSE_SOFTWARE_COST,
            value: costs.toolCost,
        },
        {
            currency,
            description: constants.OTHER_COSTS,
            value: costs.otherCosts,
        },
        {
            currency,
            description: constants.TOTAL,
            format: {
                textStyle: 'h2',
            },
            value: costs.totalCost,
        },
    ];
}

function awsCostsToRows(currency: string, costs: MainframeAWSCosts): SummaryRow[] {
    return [
        {
            currency,
            description: constants.DISASTER_RECOVERY,
            value: costs?.disasterRecoveryCost,
        },
        {
            currency,
            description: constants.AWS_INFRASTRUCTURE,
            value: costs?.infrastructureCost,
        },
        {
            currency,
            description: constants.AWS_SUPPORT,
            value: costs?.supportCost,
        },
        {
            currency,
            description: constants.THIRD_PARTY_SOFTWARE,
            value: costs?.mainframe3rdPartyCost,
        },
        {
            currency,
            description: constants.PERSONNEL,
            value: costs?.laborCost,
        },
        {
            currency,
            description: constants.OTHER_COSTS,
            value: costs?.otherCosts,
        },
        {
            currency,
            description: constants.TOTAL,
            format: {
                textStyle: 'h2',
            },
            value: costs?.totalCost,
        },
    ];
}

const onPremCostsTable = (onPremCosts: SummaryRow[]) => (
    <AWSCATTable
        resourceName={constants.MAINFRAME_WORKLOAD_ANNUAL_COST}
        columnDefinitions={MainframeSummaryCostColumnDefinitions}
        items={onPremCosts}
    />
);

const migrationCostsTable = (migrationCosts: SummaryRow[]) => (
    <AWSCATTable
        resourceName={constants.MIGRATION_COST}
        columnDefinitions={MainframeSummaryCostColumnDefinitions}
        items={migrationCosts}
    />
);

const awsCostsTable = (awsCosts: SummaryRow[]) => (
    <AWSCATTable
        resourceName={constants.AWS_ANNUAL_COST}
        columnDefinitions={MainframeSummaryCostColumnDefinitions}
        items={awsCosts}
    />
);

export default function(props: SummaryTabProps) {
    const onPremCosts = onPremCostsToRows(props.currency, props.onPremCosts);
    const migrationCosts = migrationCostsToRows(props.currency, props.migrationCosts);
    const awsCosts = awsCostsToRows(props.currency, props.awsCosts);

    const migrationCostAlert = (
        <Alert
            type='warning'
            header={constants.PLEASE_NOTE}
        >
            The migration cost is a rough order of magnitude estimate.
            For a formal migration quote, contact AWS Professional Services or a Global System Integrator.
        </Alert>
    );

    return (
        <SpaceBetween direction='vertical' size='m'>
            {awsCostsTable(awsCosts)}
            {onPremCostsTable(onPremCosts)}
            {migrationCostAlert}
            {migrationCostsTable(migrationCosts)}
        </SpaceBetween>
    );
}
